package com.eladfinish.bible.contest.screens.search.usecases.specialSearch

import com.eladfinish.bible.contest.constants.StringConstants.COLON_SPLITTER
import com.eladfinish.bible.contest.screens.search.model.ChapterDetails

/*
פירוש המצבים:

• BASE_ONLY
דורש רק את אותיות הא"ב הרגילות
כלומר: כ׳ לא יכולה להיספר מתוך ך׳

• BASE_OR_FINAL_EQUIVALENT
דורש את אותיות הא"ב, אבל אות רגילה ואות סופית נחשבות שקולות
כלומר: כ׳ או ך׳ מספיקים

• BASE_AND_FINAL_REQUIRED
דורש גם את כל האותיות הרגילות וגם את כל הסופיות בפועל
 */

enum class HebrewLetterSearchMode(
    val priority: Int,
) {
    BASE_OR_FINAL_EQUIVALENT(priority = 0),   // הכי קל
    BASE_ONLY(priority = 1),
    BASE_AND_FINAL_REQUIRED(priority = 2),   // הכי מחמיר
}

class ContainsAllHebrewLetters(
    private val mode: HebrewLetterSearchMode,
) : SpecialSearch {

    private val sb = StringBuilder()

    private var counter = 0

    override fun process(chapterDetails: ChapterDetails) = with(chapterDetails) {
        verses.forEachIndexed { verseIndex, verse ->
            if (containsAllHebrewLetters(text = verse)) {
                counter++
                sb.append(counter)
                    .append(") ")
                    .append(verse)
                    .append(COLON_SPLITTER)
                    .append(" (")
                    .append(getSource(verseIndex))
                    .append(")")
                    .append("\n")
            }
        }
    }

    override fun getSummary(): String {
        return sb.toString()
    }

    private fun containsAllHebrewLetters(
        text: String,
    ): Boolean {
        val lettersInText = text
            .asSequence()
            .filter { it in HEBREW_LETTER_RANGE }
            .toSet()

        return when (mode) {
            HebrewLetterSearchMode.BASE_ONLY -> {
                BASE_LETTERS.all(lettersInText::contains)
            }

            HebrewLetterSearchMode.BASE_OR_FINAL_EQUIVALENT -> {
                BASE_LETTERS.all { baseLetter ->
                    baseLetter in lettersInText || baseLetter.toFinalForm() in lettersInText
                }
            }

            HebrewLetterSearchMode.BASE_AND_FINAL_REQUIRED -> {
                BASE_LETTERS.all(lettersInText::contains) &&
                        FINAL_LETTERS.all(lettersInText::contains)
            }
        }
    }

    private fun Char.toFinalForm(): Char? = when (this) {
        'כ' -> 'ך'
        'מ' -> 'ם'
        'נ' -> 'ן'
        'פ' -> 'ף'
        'צ' -> 'ץ'
        else -> null
    }

    private companion object {
        val HEBREW_LETTER_RANGE = 'א'..'ת'
        val FINAL_LETTERS = "ךםןףץ".toSet()
        val BASE_LETTERS = HEBREW_LETTER_RANGE.toSet() - FINAL_LETTERS
    }
}
